import { Boundaries } from "@planzone/core";
import { projectConfiguration } from "@planzone/dto";
import { events } from "@planzone/events";
import { createStateReducer } from "../../utils/createStateReducer";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { State, Selector } from "./types";
import { createSelector } from "./selector";

const initialState: State = [];

function handleRequest(
  state: State,
  project: Boundaries["retrieveProjectToolsConfiguration"]["request"],
): State {
  const index = state.findIndex((req) => req.project.uuid === project.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          project,
          request: setRequestStateToPending(),
          configuration: projectConfiguration,
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          project,
          request: setRequestStateToPending(),
          configuration: projectConfiguration,
        },
      ];
}

function handleSuccess(
  state: State,
  {
    project,
    config,
  }: Boundaries["retrieveProjectToolsConfiguration"]["success"],
): State {
  const index = state.findIndex((req) => req.project.uuid === project.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          project,
          request: setRequestStateToSuccess(),
          configuration: config,
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          project,
          request: setRequestStateToSuccess(),
          configuration: projectConfiguration,
        },
      ];
}

function handleFailure(
  state: State,
  project: Boundaries["retrieveProjectToolsConfiguration"]["failure"],
): State {
  const index = state.findIndex((req) => req.project.uuid === project.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          project,
          request: setRequestStateToFailure(),
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          project,
          request: setRequestStateToFailure(),
          configuration: projectConfiguration,
        },
      ];
}

function handleConfigurationRequest(
  state: State,
  { project, nextConfig }: Boundaries["configureProjectTools"]["request"],
): State {
  const index = state.findIndex((req) => req.project.uuid === project.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          project,
          request: setRequestStateToPending(),
          configuration: nextConfig,
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          project,
          request: setRequestStateToPending(),
          configuration: nextConfig,
        },
      ];
}

function handleConfigurationSuccess(
  state: State,
  { project, config }: Boundaries["configureProjectTools"]["success"],
): State {
  const index = state.findIndex((req) => req.project.uuid === project.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          project,
          request: setRequestStateToSuccess(),
          configuration: config,
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          project,
          request: setRequestStateToSuccess(),
          configuration: config,
        },
      ];
}

function handleConfigurationFailure(
  state: State,
  { project, config }: Boundaries["configureProjectTools"]["failure"],
): State {
  const index = state.findIndex((req) => req.project.uuid === project.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          project,
          request: setRequestStateToFailure(),
          configuration: config,
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          project,
          request: setRequestStateToFailure(),
          configuration: config,
        },
      ];
}

const reduceState = createStateReducer(initialState, {
  [events.retrieveProjectToolsConfigurationRequested]: handleRequest,
  [events.retrieveProjectToolsConfigurationSucceeded]: handleSuccess,
  [events.retrieveProjectToolsConfigurationFailed]: handleFailure,
  [events.configureProjectToolsRequested]: handleConfigurationRequest,
  [events.configureProjectToolsSucceeded]: handleConfigurationSuccess,
  [events.configureProjectToolsFailed]: handleConfigurationFailure,
});

export type { State, Selector };

export { reduceState, initialState, createSelector };
